import React from "react";
import { observer, inject } from "mobx-react";
import { observable, computed } from "mobx";
import { Grid, Typography, Tooltip, ClickAwayListener } from "@material-ui/core";
import SimpleModal from "@src/Components/Common/SimpleModal/SimpleModal";
import { Values } from "Tools/Values";
import { IAuthStore } from "@src/Stores/Auth/types";
import { STGrid, STButton, STContentGrid, STLinkText } from "./styles";
import { STFormTitleTypography } from "@src/Components/Ui/STFormTitleTypohraphy";
import { IManagementStore } from "@src/Stores/Management/types";
import { Person } from "@src/Interfaces/Entity/Person";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import copy from "copy-to-clipboard";
import moment from "moment-timezone";
import { Toast } from "@src/Tools/Toast";
import { IValuesStore } from "@src/Stores/Values/types";

interface IProps {
  auth?: IAuthStore;
  management?: IManagementStore;
  values?: IValuesStore;
  person: Person;
}

@inject("auth", "management", "values")
@observer
export default class ResetUserPassword extends React.Component<IProps> {
  @observable opened: boolean = false;
  @observable isCopiedTooltipOpened: boolean = false;
  private toast = new Toast();

  @computed get isLinkGenerated() {
    return (
      this.props.person.resetPassword &&
      this.props.person.resetPassword.resetLink &&
      moment(this.props.person.resetPassword.validFor) >= moment().utc()
    );
  }

  @computed get lastResetDate() {
    const currentTimeZone = this.props.auth.user.location.timeZone;
    const resetDate = this.props.person.resetPassword.lastResetDate;
    return currentTimeZone
      ? moment(resetDate).utc().tz(currentTimeZone).format("HH:mm DD.MM.YYYY")
      : moment(resetDate).utc().format("HH:mm DD.MM.YYYY");
  }

  @computed get lastResetInitiatedBy() {
    const person = this.props.management.persons.find(
      (person) => person.id === this.props.person.resetPassword.lastResetInitiatedBy
    );
    return person ? person.fullName : "";
  }

  @computed get validFor() {
    const validFor = this.props.person.resetPassword.validFor;
    const duration = moment.duration(moment(validFor).diff(moment().utc()));
    return this.getDurationDateString(duration);
  }

  @computed get currentPasswordAge() {
    const passwordUpdateTime = this.props.person.passwordUpdateTime;
    const duration = moment.duration(moment().utc().diff(passwordUpdateTime));
    return this.getDurationDateString(duration);
  }

  getDurationDateString = (duration: any) => {
    let resultString = "";
    const months = duration.months();
    if (months !== 0) {
      resultString += `${months}${Values.getLocalizationValue("months")} `;
    }
    const days = duration.days();
    if (days !== 0) {
      resultString += `${days}${Values.getLocalizationValue("days")} `;
    }
    const hours = duration.hours();
    if (hours !== 0) {
      resultString += `${hours}${Values.getLocalizationValue("hours")} `;
    }
    const minutes = duration.minutes();
    if (minutes !== 0) {
      resultString += `${minutes}${Values.getLocalizationValue("minutes")}`;
    }
    return resultString;
  };

  handleOpen = () => {
    this.opened = true;
  };

  handleClose = () => {
    this.opened = false;
  };

  handleGenerateLnk = async () => {
    await this.props.management.generatePasswordResetLink(this.props.person.id);
  };

  handleDeleteReselLink = async () => {
    await this.props.management.deletePasswordResetLink(this.props.person.id);
  };

  handleSentLinkToUserMail = async () => {
    var result = await this.props.management.sentResetLinkToUserMail(this.props.person.name, this.props.person.email);
    if (!result) {
      this.toast.showError(Values.getLocalizationValue("passwordResetLinkNotSentToMail"));
      return;
    }
    this.toast.showSuccess(Values.getLocalizationValue("passwordResetLinkSentToMail"));
  };

  handleCopy = () => {
    copy(this.props.person.resetPassword.resetLink);
    this.isCopiedTooltipOpened = true;
  };

  handleCopiedTooltipClose = () => {
    this.isCopiedTooltipOpened = false;
  };

  render() {
    return (
      <React.Fragment>
        <STButton
          variant="contained"
          color="default"
          onClick={this.handleOpen}
          //disabled={!this.props.auth.user.location.primary}
        >
          {Values.getLocalizationValue("resetUserPassword")}
        </STButton>
        <SimpleModal width={650} isOpened={this.opened} onClose={this.handleClose}>
          <STGrid container>
            <Grid container justify="center">
              <STFormTitleTypography>{Values.getLocalizationValue("resetUserPassword")}</STFormTitleTypography>
            </Grid>
            <STContentGrid container justify="flex-start">
              <Grid item xs={12}>
                {this.isLinkGenerated ? (
                  <Grid container>
                    <Grid item xs={2}>
                      <Typography variant="body1">{`${Values.getLocalizationValue("resetLink")}:`}</Typography>
                    </Grid>
                    <Grid item xs={10} style={{ display: "flex" }}>
                      {this.props.person.resetPassword && (
                        <>
                          <STLinkText variant="body1">{this.props.person.resetPassword.resetLink}</STLinkText>
                          <ClickAwayListener onClickAway={this.handleCopiedTooltipClose}>
                            <Tooltip
                              PopperProps={{
                                disablePortal: true,
                              }}
                              onClose={this.handleCopiedTooltipClose}
                              open={this.isCopiedTooltipOpened}
                              disableFocusListener
                              disableHoverListener
                              disableTouchListener
                              title={Values.getLocalizationValue("copied")}
                            >
                              <FileCopyOutlinedIcon style={{ cursor: "pointer" }} onClick={this.handleCopy} />
                            </Tooltip>
                          </ClickAwayListener>
                        </>
                      )}
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container>
                    <Grid item xs={4}>
                      <Typography variant="body1">{`${Values.getLocalizationValue("lastPasswordReset")}:`}</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      {this.props.person.resetPassword && this.props.person.resetPassword.lastResetDate && (
                        <Typography variant="body1">{`${this.lastResetDate} (${Values.getLocalizationValue(
                          "initiatedBy"
                        )} ${this.lastResetInitiatedBy})`}</Typography>
                      )}
                    </Grid>
                  </Grid>
                )}
              </Grid>
              <Grid item xs={12}>
                {this.isLinkGenerated ? (
                  <Grid container>
                    <Grid item xs={2}>
                      <Typography variant="body1">{`${Values.getLocalizationValue("validFor")}:`}</Typography>
                    </Grid>
                    <Grid item xs={10}>
                      {this.props.person.resetPassword && <Typography variant="body1">{this.validFor}</Typography>}
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container>
                    <Grid item xs={4}>
                      <Typography variant="body1">{`${Values.getLocalizationValue("currentPasswordAge")}:`}</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body1">{this.currentPasswordAge}</Typography>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </STContentGrid>
            <Grid container justify="flex-end">
              <STButton onClick={this.handleClose} variant="contained" style={{ marginRight: "auto" }} v>
                {Values.getLocalizationValue("cancel")}
              </STButton>
              {this.isLinkGenerated ? (
                <>
                  <STButton onClick={this.handleDeleteReselLink} variant="contained" color="default">
                    {Values.getLocalizationValue("deleteResetLink")}
                  </STButton>
                  {this.props.values.isEmailSettingsConfigured && (
                    <STButton onClick={this.handleSentLinkToUserMail} variant="contained" color="default">
                      {Values.getLocalizationValue("sentLinkToUserMail")}
                    </STButton>
                  )}
                </>
              ) : (
                <STButton onClick={this.handleGenerateLnk} variant="contained" color="default">
                  {Values.getLocalizationValue("generatePasswordResetLink")}
                </STButton>
              )}
            </Grid>
          </STGrid>
        </SimpleModal>
      </React.Fragment>
    );
  }
}
